import { useState, useRef, useEffect } from "react";
import {
    createUppy,
    addUppyPlugins,
    getUppyLocale,
    addUppyEvents,
    removeUppyEvents,
    UppyEvent,
    uppyPendingFilesMetaSelector,
} from "data/uppy";
import { DroppedFile } from "data/types";
import { useDeepCompareEffect } from "hooks";
import { useSelector } from "react-redux";

type Props = {
    /** which uppy ID to use (to separate from other upload queues)
     * @default {string} const from data/uppy.js/DEFAULT_UPPY_ID
     */
    uppyId?: string;
    /** optional uppy properties to pass (e.g. maxNumberOfFiles) */
    uppyProps?: { maxNumberOfFiles: number; mode: string };
    /** optional list of uppy events to attach to */
    events?: UppyEvent[];
    /** files to upload */
    files?: DroppedFile[];
};

export function useUppy({ uppyId, uppyProps, events, files }: Props) {
    const [ready, setReady] = useState(false);
    const uppy = useRef<any>(null);

    useEffect(() => {
        uppy.current = createUppy({
            id: uppyId,
            ...uppyProps,
        });
        uppy.current = addUppyPlugins(uppy.current);
        // cancel all pending uploads in this queue
        uppy.current.cancelAll();
        if (events) addUppyEvents(events, uppy.current);
        getUppyLocale()
            .then((result) => {
                uppy.current.setOptions({ locale: result.i18n });
            })
            .finally(() => setReady(true));

        return () => {
            if (events) removeUppyEvents(events, uppy.current);
            uppy.current.close();
        };
    }, [uppyId, uppyProps, events, files]);

    useEffect(() => {
        if (!ready || !files?.length || uppy.current == null) return;
        // add optional files to queue
        /* istanbul ignore next */
        try {
            files.forEach((file) => {
                /**
                 * if a immutable object is supplied, we need to open it up,
                 * due to the nature of uppy modifying the original object
                 */
                uppy.current.addFile({
                    ...file,
                    meta: {
                        ...file.meta,
                    },
                });
            });
        } catch (e) {
            console.error("Cannot add file to uppy queue", e);
        }
    }, [ready, files]);

    return ready ? uppy.current : null;
}

/**
 * data/uppy/uppyPendingFilesMetaSelector returns a new object on every call
 * this wrapper uses a deep compare to avoid re-renders
 * @returns {Array<uppyFile>} array of pending file uploads
 */
export function usePendingUploads(): any[] {
    const [pendingUploads, setPendingUploads] = useState<any[]>([]);
    const uppyPendingUploads = useSelector(uppyPendingFilesMetaSelector);

    useDeepCompareEffect(() => {
        setPendingUploads(uppyPendingUploads);
    }, [uppyPendingUploads]);

    return pendingUploads;
}
